import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * WallPreview — pure-CSS "on the wall" mockup so buyers can picture a print
 * before purchase. No stock room photo needed; we render a warm, softly-lit
 * neutral wall with a thin dark frame and a cream matte around the print.
 */
export const WallPreview = ({ open, onClose, imageUrl, title, sizeLabel }) => {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[75] flex items-center justify-center p-4 sm:p-10"
      onClick={onClose}
      role="dialog"
      aria-label="Preview on the wall"
      data-testid="wall-preview-modal"
      style={{
        // Warm neutral wall with a soft top-down vignette and a whisper of
        // baseboard shadow so the frame reads as sitting *on* something.
        background:
          'radial-gradient(120% 80% at 50% 0%, #efe4d3 0%, #e2d4bf 45%, #c9b697 100%)',
      }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close preview"
        className="absolute top-5 right-5 z-10 w-11 h-11 flex items-center justify-center rounded-full bg-ink/10 hover:bg-ink/20 text-ink backdrop-blur-sm transition-colors"
        data-testid="wall-preview-close"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Baseboard shadow strip */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-ink/25 to-transparent pointer-events-none" />

      {/* Frame + matte + print */}
      <div
        onClick={(e) => e.stopPropagation()}
        className={cn(
          'relative bg-ink/95',
          'p-2 sm:p-3',
          // Multi-layered shadow so it looks like it's hanging, not floating
          'shadow-[0_20px_60px_-15px_rgba(0,0,0,0.55),0_8px_22px_-8px_rgba(0,0,0,0.35)]'
        )}
        style={{ maxWidth: 'min(70vw, 720px)', maxHeight: '80vh' }}
      >
        {/* cream matte */}
        <div className="bg-[#f6efe1] p-6 sm:p-10">
          <img
            src={imageUrl}
            alt={title}
            className="block max-w-full max-h-[55vh] object-contain"
          />
        </div>
      </div>

      {/* Caption line */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center pointer-events-none">
        <p
          className="text-xs uppercase tracking-[0.24em] text-ink/70"
          data-testid="wall-preview-caption"
        >
          {title}
          {sizeLabel ? <span className="text-ink/50"> · {sizeLabel}</span> : null}
        </p>
      </div>
    </div>
  );
};

export default WallPreview;
